import { UserRequestRole } from "../components/Forms/UserRequest/_core/_models"
import { RoleGroup } from "../components/Roles/_core/_model"


export interface GetUserRequest {
    userID: number, userName: string | null, email: string, roleID: string | null, roleGroupID: string | null, managerUserID: string | null, appliationAccess: string | null, 
}

export interface DimensionModel {
    dimensionID: number,
    dimensionName: string,
    dimensionCode: string,
    warehouse: string,
    accessType: string,
    bg: string,
    color: string,
    byUser: number,
    dirtyFields: string[]
}

export interface DimensionFieldsModel {
    columnID: number,
    columnName: string,
    columnDescription: string,
    forDimension: string,
    forDirectUser: number,
    fieldType: number
}

export interface RoleRequestParams {
    roleID: string | null, roleName: string | null, roleCode: string, dataMapColumn: string | null, roleGroupID: string | null, userID: string | null, typeOfRoles: string
}

export interface RoleResponseModel {
    roleID: number | null,
    roleName: string,
    roleCode: string,
    effectiveDate: string,
    status: string,
    roleType: string,
    description: string,
    dataMapCode: string,
    notes: string,
    dimensionName: string,
    weight: number,
    createdBy: number,
    dimensionID: number,
    createdAt?: string,
    filterConditions: string,
    _source: string,
    dirtyFields: string[]
}

export interface FilterResponseModel {
    filterID: number,
    filterName: string,
    filterCode: string,
    status: string,
    filterConditions: string,
    createdAt: string,
    roleID: number
}

export interface APIState {
    progress: boolean,
    success: boolean,
    message: string
}

export interface UserRequestGetParam {
    stagingUserID: number | null,
    userName: string | null,
    status: string | null,
    userRequestedBy: string | null,
    userApprovedBy: string | null,
    createdAt: string | null,
    approvedAt: string | null
}

export interface UserRequestTable {
    stagingUserID: number,
    managerUserID: number,
    managerExt: string,
    userFirstName: string,
    userName: string,
    userLastName: string,
    email: string,
    role: string,
    userExt: string,
    boNeeded: boolean,
    requestDate: string,
    replacingFormerUser: boolean,
    formerUserID: number,
    status: string,
    statusComment: string,
    applicationAccess: number,
    bOLicenceUserID: number,
    internationalOnly: number,
    customerDimension: number,
    createdAt: string,
    approvedAt: string,
    dimesionsRoles: { [key: string]: UserRequestRole[] },
    sections: { [key: string]: boolean },
    userRequestedBy: number,
    userApprovedBy: number,
    roleGroups: string,
    isAdUser: number,
    aComment: string,
    rComment: string,
    replaceRoles: boolean,
    userID: number;
}

export interface ApproveRejctRequest {
    stagingUserID: number,
    rComment: string
    aComment: string,
    action: number,
    symabolicID: string,
    userIDAlias: string,
    userName: string,
    email: string,
    password: string,
    languageCode: string,
    currencyCode: string,
    defaultMobilePage: string,
    enableExpertEntry: number,
    navigatorHomepage: string,
    processProfile: string,
    primary: string,
    rowSecurity: number,
    userRequestedBy: number,
    userApprovedBy: number,
    dimesionsRoles: { [key: string]: UserRequestRole[] },
    internationalOnly: number,
    customerDimension: number,
    roleGroups: number[],
    replaceRoles: boolean,
    dirtyFields: string[],
    userID: number;
}

export interface AppUserResponse {
    appUserID: number,
    appUserName: string,
    appUserFirstName: string,
    appUserLastName: string,
    appEmail: string,
    appPasswordHash: string,
    appRole: string,
    appStatus: string,
    appManagerUserID: number,
    createdBy: number,
    createdAt: string
}

export interface UserModel {
    userID: number,
    userName: string
    userFirstName: string,
    userLastName: string,
    email: string,
    password: string,
    passwordHash: string,
    role: string,
    product: string,
    status: string,
    createdBy: number,
    createdAt: string,
    isAdUser: number,
    applicationAccess: number
}

export interface UserRoles {
    roleID: number,
    userID: number,
    dataMapCode: string
}

export interface UpsertUserRoleRequest {
    userID: number,
    userRoles: { [key: string]: number[] },
    byUser: number,
    prevRoles: number[]
}

export interface AuditItem {
    aAuditItemId: number,
    forInstanceName: string,
    forObject: AuditObjectTypes,
    forInstance: number,
    byUser: number,
    onDate: string,
    actionType: AuditActionType
}

export enum AuditActionType {
    Add, Update, Delete, UserRole, UserRoleGroup
}

export function GetActionTypeString(acType: AuditActionType) {
    let t = "";
    switch (acType) {
        case AuditActionType.Add:
            t = "Add";
            break;
        case AuditActionType.Update:
            t = "Update";
            break;
        case AuditActionType.Delete:
            t = "Delete";
            break;
        case AuditActionType.UserRole:
            t = "Role";
            break;
        case AuditActionType.UserRoleGroup:
            t = "Group";
            break;
    }
    return t;
}

export interface AuditDetails {
    fieldName: string,
    fieldType: string,
    value: string,
    previousValue: string
}

export enum AuditObjectTypes {
    User, Role, RoleGroup, Settings, Report, UserRequest, ReportDistributions, EmailConfig, EmailTemplate, Dimensions, ADConfig, Flow, ColumnRestrictions
}

export interface TableColumns {
    objectFieldID: number,
    objType: AuditObjectTypes,
    field: string,
    fieldDisplayName: string,
    width: number,
    sortColumn: string,
    orderBy: number,
    userID: number,
    createdAt: string
}

export interface ReactColumn {
    accessorFn?: any,
    accessorKey?: string,
    header: string,
    size?: number, 
    enableSorting?: boolean,
    enableResizing?: boolean,
    cell?: any
}


export interface JobStage {
    stageID: string;
    stageName: string;
    status: string; // Updated to string to support flexible JSON statuses
    startTime: string;
    endTime: string;
    durationMinutes: number;
    errorMessage?: string;
}

export interface JobRunHistory {
    runID: string;
    runDate: string;
    startTime: string;
    endTime: string;
    durationMinutes: number;
    status: string; // Updated to string
    stages: JobStage[];
}

export interface NightlyJobConfigModel {
    isJobEnabled: boolean;
    scheduledTime: string; 
    jobStatus: string;
}

// Column Level Restrictions
export interface PolicyColumn {
    name: string;
    description: string;
    isVisible: boolean;
}

export interface PolicyDimension {
    dimensionCode: string;
    columns: PolicyColumn[];
}

export interface PolicyUser {
    userID: number;
    userName: string;
    displayName: string;
}

export interface ColumnSecurityPolicy {
    policyID: string;
    name: string;
    description: string;
    users: PolicyUser[];
    dimensions: PolicyDimension[];
    createdAt?: string;
    createdBy?: number;
}